import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Mail, MessageSquare, Globe, FileText, CheckCircle, ArrowRight, ArrowLeft, CreditCard, AlertCircle } from 'lucide-react';
import { bookingService, BookingCreateRequest } from '../services/api';
import { t } from '../services/i18n';
import { PayPalHostedButton } from './PayPalHostedButton';
// PayPal integration - using hosted buttons

interface ClientBookingProps {
  currentUser: any;
}

const CONSULTING_TYPES = [
  { value: 'seo', label: 'SEO Consulting' },
  { value: 'wordpress', label: 'WordPress Optimization' },
  { value: 'woocommerce', label: 'WooCommerce Consulting' }
] as const;

const DURATIONS = [
  { value: 30, label: '30 minutes', price: 295, popular: false },
  { value: 60, label: '1 hour', price: 450, popular: true },
  { value: 90, label: '1.5 hours', price: 575, popular: false }
] as const;

const ClientBooking: React.FC<ClientBookingProps> = ({ currentUser }) => {
  const [step, setStep] = useState(1);
  const [consultingType, setConsultingType] = useState<'seo' | 'wordpress' | 'woocommerce'>('seo');
  const [duration, setDuration] = useState<30 | 60 | 90>(60);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Form data
  const [fullName, setFullName] = useState(currentUser?.name || '');
  const [email, setEmail] = useState(currentUser?.email || '');
  const [telegram, setTelegram] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [topic, setTopic] = useState('');
  const [legalConfirmation, setLegalConfirmation] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paypalOrderId, setPaypalOrderId] = useState<string | null>(null);
  const [paypalApprovalUrl, setPaypalApprovalUrl] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);

  // Initialize date to tomorrow
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setSelectedDate(tomorrow.toISOString().split('T')[0]);
  }, []);

  // Load available slots when date or duration changes
  useEffect(() => {
    if (selectedDate && duration) {
      loadAvailableSlots();
    }
  }, [selectedDate, duration]);

  const loadAvailableSlots = async () => {
    if (!selectedDate || !duration) return;
    
    setLoadingSlots(true);
    setError('');
    try {
      const result = await bookingService.getAvailability(selectedDate, duration);
      setAvailableSlots(result.availableSlots);
    } catch (err: any) {
      setError(err.message || 'Failed to load available slots');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleNext = () => {
    if (step === 1 && !consultingType) {
      setError('Please select a consulting type');
      return;
    }
    if (step === 2 && (!selectedDate || !selectedTime)) {
      setError('Please select a date and time');
      return;
    }
    setError('');
    setStep(step + 1);
  };

  const handleBack = () => {
    setError('');
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!fullName || !email || !telegram || !topic || topic.length < 50 || !legalConfirmation || !termsAccepted) {
      setError('Please fill all required fields and accept terms');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Create a pending booking that will be confirmed when payment is received via webhook
      const bookingData: BookingCreateRequest = {
        consultingType,
        duration,
        date: selectedDate,
        startTime: selectedTime,
        clientInfo: {
          fullName,
          email,
          telegram,
          phone: phone || undefined,
          website: website || undefined
        },
        consultationDetails: {
          topic,
          legalConfirmation: 'true',
          termsAccepted: 'true'
        }
      };

      // Create booking with pending payment status
      const result = await bookingService.createBooking(bookingData);
      setBookingId(result.bookingId);
      setStep(5); // Move to payment step
    } catch (err: any) {
      console.error('Booking creation error:', err);
      let errorMessage = 'Failed to create booking';
      
      if (err.message) {
        errorMessage = err.message;
      } else if (err.error) {
        errorMessage = err.error;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      
      // Check for validation errors
      if (err.errors && Array.isArray(err.errors)) {
        const validationErrors = err.errors.map((e: any) => e.msg || e.message).join(', ');
        errorMessage = `Validation error: ${validationErrors}`;
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePayPalApprove = async (data: any, actions: any) => {
    if (!bookingId || !data.orderID) {
      setError('Missing booking or order information');
      return;
    }

    try {
      const result = await bookingService.verifyPayment(bookingId, data.orderID);
      if (result.success) {
        setSuccess(true);
        setStep(6); // Success step
      } else {
        setError('Payment verification failed');
      }
    } catch (err: any) {
      setError(err.message || 'Payment verification failed');
    }
  };

  // Handle PayPal redirect (when user clicks approval URL)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const bookingIdParam = urlParams.get('bookingId');
    const status = urlParams.get('status');

    if (bookingIdParam && status === 'success') {
      // User returned from PayPal, verify payment
      const orderId = urlParams.get('token') || urlParams.get('PayerID');
      if (orderId && bookingIdParam) {
        setBookingId(bookingIdParam);
        bookingService.verifyPayment(bookingIdParam, orderId)
          .then((result) => {
            if (result.success) {
              setSuccess(true);
              setStep(6);
              // Clean URL
              window.history.replaceState({}, '', window.location.pathname);
            } else {
              setError('Payment verification failed');
            }
          })
          .catch((err: any) => {
            setError(err.message || 'Payment verification failed');
          });
      }
    }
  }, []);

  const selectedPrice = DURATIONS.find(d => d.value === duration)?.price || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">{t('consulting.title') || 'Book a Consulting Session'}</h1>
          <p className="text-slate-400">{t('consulting.subtitle') || 'Get expert SEO, WordPress, and WooCommerce advice'}</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3, 4, 5, 6].map((s) => (
              <div key={s} className={`flex-1 h-2 rounded-full mx-1 ${step >= s ? 'bg-indigo-600' : 'bg-slate-700'}`} />
            ))}
          </div>
          <div className="flex justify-between text-xs text-slate-400">
            <span>{t('consulting.step1') || 'Service'}</span>
            <span>{t('consulting.step2') || 'Date & Time'}</span>
            <span>{t('consulting.step3') || 'Details'}</span>
            <span>{t('consulting.step4') || 'Review'}</span>
            <span>{t('consulting.step5') || 'Payment'}</span>
            <span>{t('consulting.step6') || 'Confirm'}</span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300 flex items-center gap-2">
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        {/* Step 1: Service & Duration */}
        {step === 1 && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 animate-fadeIn">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <FileText size={24} />
              {t('consulting.selectService') || 'Select Consulting Type'}
            </h2>
            
            <div className="space-y-4 mb-8">
              {CONSULTING_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setConsultingType(type.value)}
                  className={`w-full p-6 rounded-xl border-2 text-left transition-all ${
                    consultingType === type.value
                      ? 'border-indigo-500 bg-indigo-500/20'
                      : 'border-slate-700 hover:border-slate-600 bg-slate-800/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-semibold ${consultingType === type.value ? 'text-white' : 'text-slate-300'}`}>
                      {type.label}
                    </span>
                    {consultingType === type.value && <CheckCircle className="text-indigo-400" size={24} />}
                  </div>
                </button>
              ))}
            </div>

            <h3 className="text-xl font-bold text-white mb-4">{t('consulting.selectDuration') || 'Select Duration'}</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {DURATIONS.map((d) => (
                <button
                  key={d.value}
                  onClick={() => setDuration(d.value)}
                  className={`p-6 rounded-xl border-2 transition-all relative ${
                    duration === d.value
                      ? 'border-indigo-500 bg-indigo-500/20'
                      : 'border-slate-700 hover:border-slate-600 bg-slate-800/50'
                  }`}
                >
                  {d.popular && (
                    <span className="absolute top-2 right-2 bg-indigo-600 text-white text-xs px-2 py-1 rounded">
                      {t('consulting.popular') || 'POPULAR'}
                    </span>
                  )}
                  <div className="text-center">
                    <div className={`text-2xl font-bold mb-2 ${duration === d.value ? 'text-white' : 'text-slate-300'}`}>
                      {d.label}
                    </div>
                    <div className={`text-3xl font-extrabold ${duration === d.value ? 'text-indigo-400' : 'text-slate-400'}`}>
                      {d.price} €
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex justify-end mt-8">
              <button
                onClick={handleNext}
                className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all flex items-center gap-2"
              >
                {t('consulting.next') || 'Next'} <ArrowRight size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Date & Time */}
        {step === 2 && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 animate-fadeIn">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Calendar size={24} />
              {t('consulting.selectDateTime') || 'Select Date & Time'}
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <label className="block text-slate-300 font-semibold mb-3">
                  {t('consulting.selectDate') || 'Select Date'}
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setSelectedTime('');
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full p-4 bg-slate-800 border border-slate-700 text-white rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                />
                <p className="text-xs text-slate-400 mt-2">
                  {t('consulting.timezone') || 'Your Local Time'}
                </p>
              </div>

              <div>
                <label className="block text-slate-300 font-semibold mb-3">
                  {t('consulting.selectTime') || 'Select Time'}
                </label>
                {loadingSlots ? (
                  <div className="text-slate-400 text-center p-8">{t('consulting.loadingSlots') || 'Loading available slots...'}</div>
                ) : availableSlots.length === 0 ? (
                  <div className="text-slate-400 text-center p-8">{t('consulting.noSlots') || 'No available slots for this date'}</div>
                ) : (
                  <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot}
                        onClick={() => setSelectedTime(slot)}
                        className={`p-3 rounded-lg border transition-all ${
                          selectedTime === slot
                            ? 'border-indigo-500 bg-indigo-500/20 text-white'
                            : 'border-slate-700 hover:border-slate-600 bg-slate-800/50 text-slate-300'
                        }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-between mt-8">
              <button
                onClick={handleBack}
                className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-all flex items-center gap-2"
              >
                <ArrowLeft size={20} /> {t('consulting.back') || 'Back'}
              </button>
              <button
                onClick={handleNext}
                disabled={!selectedDate || !selectedTime}
                className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('consulting.next') || 'Next'} <ArrowRight size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: User Details */}
        {step === 3 && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 animate-fadeIn">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <User size={24} />
              {t('consulting.yourDetails') || 'Your Details'}
            </h2>

            <div className="space-y-6">
              <div>
                <label className="block text-slate-300 font-semibold mb-2">
                  {t('consulting.fullName') || 'Full Name'} *
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full p-4 bg-slate-800 border border-slate-700 text-white rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-slate-300 font-semibold mb-2">
                  <Mail size={16} className="inline mr-2" />
                  {t('consulting.email') || 'Email Address'} *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-4 bg-slate-800 border border-slate-700 text-white rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-slate-300 font-semibold mb-2">
                  <MessageSquare size={16} className="inline mr-2" />
                  {t('consulting.telegram') || 'Telegram Username'} * (@username)
                </label>
                <input
                  type="text"
                  value={telegram}
                  onChange={(e) => setTelegram(e.target.value)}
                  placeholder="@username"
                  className="w-full p-4 bg-slate-800 border border-slate-700 text-white rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-slate-300 font-semibold mb-2">
                  {(() => {
                    const phoneLabel = t('consulting.phone');
                    const optionalLabel = t('consulting.optional');
                    return phoneLabel !== 'consulting.phone' 
                      ? `${phoneLabel} (${optionalLabel !== 'consulting.optional' ? optionalLabel : 'optional'})`
                      : 'Phone Number (optional)';
                  })()}
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full p-4 bg-slate-800 border border-slate-700 text-white rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-semibold mb-2">
                  <Globe size={16} className="inline mr-2" />
                  {(() => {
                    const websiteLabel = t('consulting.website');
                    const optionalLabel = t('consulting.optional');
                    return websiteLabel !== 'consulting.website'
                      ? `${websiteLabel} (${optionalLabel !== 'consulting.optional' ? optionalLabel : 'optional'})`
                      : 'Website URL (optional)';
                  })()}
                </label>
                <input
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://..."
                  className="w-full p-4 bg-slate-800 border border-slate-700 text-white rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-semibold mb-2">
                  {t('consulting.topic') || 'Consultation Topic'} * (50-1000 characters)
                </label>
                <textarea
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  rows={6}
                  minLength={50}
                  maxLength={1000}
                  className="w-full p-4 bg-slate-800 border border-slate-700 text-white rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder={t('consulting.topicPlaceholder') || 'Describe what you need help with, your business goals, and what you\'ve tried so far...'}
                  required
                />
                <div className="text-xs text-slate-400 mt-2">
                  {topic.length} / 1000 {t('consulting.characters') || 'characters'}
                </div>
              </div>

              <div className="space-y-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={legalConfirmation}
                    onChange={(e) => setLegalConfirmation(e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-slate-600 bg-slate-800 text-indigo-600 focus:ring-indigo-500"
                    required
                  />
                  <span className="text-slate-300 text-sm">
                    {t('consulting.legalConfirmation') || 'I confirm my business is legal and complies with accepted topics (IPTV services, Windows/Office/QuickBooks licensing, physical products, legal digital products, Shopify, WordPress/WooCommerce)'} *
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-slate-600 bg-slate-800 text-indigo-600 focus:ring-indigo-500"
                    required
                  />
                  <span className="text-slate-300 text-sm">
                    {t('consulting.termsAccepted') || 'I agree to the terms and conditions including cancellation policy'} *
                  </span>
                </label>
              </div>
            </div>

            <div className="flex justify-between mt-8">
              <button
                onClick={handleBack}
                className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-all flex items-center gap-2"
              >
                <ArrowLeft size={20} /> {t('consulting.back') || 'Back'}
              </button>
              <button
                onClick={handleNext}
                disabled={!fullName || !email || !telegram || topic.length < 50 || !legalConfirmation || !termsAccepted}
                className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('consulting.next') || 'Next'} <ArrowRight size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {step === 4 && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 animate-fadeIn">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <CheckCircle size={24} />
              {t('consulting.review') || 'Review Your Booking'}
            </h2>

            <div className="bg-slate-800/50 rounded-xl p-6 space-y-4 mb-6">
              <div className="flex justify-between">
                <span className="text-slate-400">{t('consulting.service') || 'Service'}:</span>
                <span className="text-white font-semibold">{CONSULTING_TYPES.find(t => t.value === consultingType)?.label}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">{t('consulting.duration') || 'Duration'}:</span>
                <span className="text-white font-semibold">{DURATIONS.find(d => d.value === duration)?.label}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">{t('consulting.date') || 'Date'}:</span>
                <span className="text-white font-semibold">{new Date(selectedDate).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">{t('consulting.time') || 'Time'}:</span>
                <span className="text-white font-semibold">{selectedTime}</span>
              </div>
              <div className="flex justify-between pt-4 border-t border-slate-700">
                <span className="text-xl font-bold text-white">{t('consulting.total') || 'Total'}:</span>
                <span className="text-2xl font-extrabold text-indigo-400">{selectedPrice} €</span>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                onClick={handleBack}
                className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-all flex items-center gap-2"
              >
                <ArrowLeft size={20} /> {t('consulting.back') || 'Back'}
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all flex items-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? t('consulting.processing') || 'Processing...' : t('consulting.proceedToPayment') || 'Proceed to Payment'}
                <CreditCard size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Payment */}
        {step === 5 && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 animate-fadeIn">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <CreditCard size={24} />
              {t('consulting.payment') || 'Complete Payment'}
            </h2>

            <div className="bg-slate-800/50 rounded-xl p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <span className="text-slate-300">{t('consulting.amount') || 'Amount'}:</span>
                <span className="text-3xl font-extrabold text-indigo-400">{selectedPrice} €</span>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                {t('consulting.paymentNote') || 'Pay securely with PayPal or credit/debit card'}
              </p>
              <div className="mt-4">
                {duration === 30 && (
                  <PayPalHostedButton 
                    hostedButtonId="WYJXGPJEPBVWN"
                    onError={(error) => {
                      console.error('30min consulting payment error:', error);
                      setError('Payment button failed to load. Please try again.');
                    }}
                  />
                )}
                {duration === 60 && (
                  <PayPalHostedButton 
                    hostedButtonId="753M4ZDC97AQ6"
                    onError={(error) => {
                      console.error('1h consulting payment error:', error);
                      setError('Payment button failed to load. Please try again.');
                    }}
                  />
                )}
                {duration === 90 && (
                  <PayPalHostedButton 
                    hostedButtonId="QBRKLFSN8JX36"
                    onError={(error) => {
                      console.error('1.5h consulting payment error:', error);
                      setError('Payment button failed to load. Please try again.');
                    }}
                  />
                )}
              </div>
            </div>

            <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-xl p-4 mb-6">
              <p className="text-yellow-300 text-sm">
                {t('consulting.paymentRedirect') || 'After payment, your booking will be automatically confirmed. You will receive a confirmation email shortly.'}
              </p>
            </div>

            <button
              onClick={handleBack}
              className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition-all flex items-center gap-2"
            >
              <ArrowLeft size={20} /> {t('consulting.back') || 'Back'}
            </button>
          </div>
        )}

        {/* Step 6: Success */}
        {step === 6 && success && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 animate-fadeIn text-center">
            <CheckCircle size={64} className="text-green-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">
              {t('consulting.success') || 'Booking Confirmed!'}
            </h2>
            <p className="text-slate-300 mb-6">
              {t('consulting.successMessage') || 'Your booking has been confirmed. You will receive a confirmation email with Google Meet link shortly.'}
            </p>
            <p className="text-slate-400 text-sm mb-8">
              {t('consulting.bookingReference') || 'Booking Reference'}: <span className="font-mono text-indigo-400">#{bookingId}</span>
            </p>
            <button
              onClick={() => {
                setStep(1);
                setSuccess(false);
                setBookingId(null);
                setPaypalOrderId(null);
              }}
              className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all"
            >
              {t('consulting.bookAnother') || 'Book Another Session'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientBooking;

